import { setUserProperty } from 'utils/analytics';

const STORAGE_PREFIX = 'experiment:';

// FNV-1a 32-bit string hash — deterministic, uniform enough for 2-arm splits.
/* eslint-disable no-bitwise */
const hashString = (str) => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < str.length; i += 1) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
};
/* eslint-enable no-bitwise */

const storageKey = (name) => `${STORAGE_PREFIX}${name}`;

// The arm is stored as JSON `{ unitId, variant }` so a shared browser cannot leak one
// account's arm to the next account that signs up in it. Storage can throw (disabled
// or private mode) and this runs in the signup path, so every access is guarded.
const readAssignment = (name) => {
  try {
    const raw = localStorage.getItem(storageKey(name));
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

// Returns true only when the assignment actually reached storage.
const persistAssignment = (name, assignment) => {
  try {
    localStorage.setItem(storageKey(name), JSON.stringify(assignment));
    return true;
  } catch (e) {
    return false;
  }
};

const clearAssignment = (name) => {
  try {
    localStorage.removeItem(storageKey(name));
  } catch (e) {
    // Best effort — nothing more we can do if storage is fully unavailable.
  }
};

// The id of the account currently signed in on this device. Used to guard the variant
// read against a session swap (e.g. a magic-link login that replaces the account without
// clearing storage) so events can't inherit a previous account's arm. Stored by
// useLocalStorage, hence JSON-encoded.
const currentUnitId = () => {
  try {
    const raw = localStorage.getItem('userId');
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

// Read-only: the persisted variant, but only for the account it was assigned to. If a
// different account is now signed in, the stored arm is not theirs — return null rather
// than mislabel their events.
export const getVariant = (name) => {
  const stored = readAssignment(name);
  if (!stored) return null;

  const unit = currentUnitId();
  if (unit && stored.unitId !== unit) return null;

  return stored.variant;
};

// Deterministic per unitId. The stored arm is reused only when it belongs to THIS
// unit, so a second account signing up in the same browser is rolled fresh instead
// of inheriting the first account's arm. An assignment that cannot be persisted is
// treated as unassigned (null) rather than routing an arm that getVariant() could
// never read back — that would strand the user in a half-applied Arm A.
export const assignVariant = (name, arms, { unitId, userProperty } = {}) => {
  const stored = readAssignment(name);
  if (stored && stored.unitId === unitId) {
    if (userProperty) setUserProperty(userProperty, stored.variant);
    return stored.variant;
  }
  if (!unitId) return null;

  const variant = arms[hashString(`${name}:${unitId}`) % arms.length];
  if (!persistAssignment(name, { unitId, variant })) {
    // The new arm could not be stored. If a different unit's assignment is still on
    // disk, drop it so getVariant() (which is unit-unaware) cannot later mislabel this
    // unit with the stale value. This unit is then treated as unassigned.
    if (stored) clearAssignment(name);
    return null;
  }

  if (userProperty) setUserProperty(userProperty, variant);
  return variant;
};
